import createSelector from 'selectorator'
import { identity } from 'ramda'
import { RootState } from 'src/state/types'
import { TAgencySegment } from 'modules/api/agency/types'
import { TMaybeId } from 'modules/api/portfolio/types'

/**
 * The default segment's id is the agency id. It is what applies when no other
 * segment is toggled on, so it is never shown or managed as a segment itself.
 */
export const getDefaultSegmentId = createSelector<RootState, string | null>(['api.user.currentAgencyId'], identity)

export const isDefaultSegment = createSelector<RootState, (id: TMaybeId) => boolean>(
  ['api.user.currentAgencyId'],
  (defaultSegmentId: string | null) =>
    (id: TMaybeId): boolean =>
      !!id && id === defaultSegmentId,
)

/** Every segment on the agency except the default one */
export const getSegmentsWithoutDefault = createSelector<RootState, TAgencySegment[]>(
  ['api.agency.segments', 'api.user.currentAgencyId'],
  (segments: TAgencySegment[], defaultSegmentId: string | null): TAgencySegment[] =>
    segments.filter(segment => segment.id !== defaultSegmentId),
)

/** The non-default segments the current user is a member of, in membership order */
export const getAvailableSegments = createSelector<RootState, TAgencySegment[]>(
  ['api.agency.segments', 'api.user.segmentMemberships', 'api.user.currentAgencyId'],
  (segments: TAgencySegment[], memberships: string[], defaultSegmentId: string | null): TAgencySegment[] =>
    memberships
      .filter(id => id !== defaultSegmentId)
      .map(id => segments.find(segment => segment.id === id))
      .filter((segment): segment is TAgencySegment => segment !== undefined),
)
